export type ReservationDeviceStorage = {
	deviceID: string
	cartActive?: boolean
}

/**
 * Generates a stable device identifier value that can be persisted in browser storage
 * and reused across requests. It prefers the browser-native UUID API and falls back
 * to a random string when `crypto.randomUUID()` is not available.
 *
 * This function should be used for client-side identifiers only. The returned value is
 * intentionally opaque and should be stored by the caller if it needs to survive reloads.
 */
export const generateReservationDeviceID = () => {
	if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
		return crypto.randomUUID()
	}

	return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`
}

type ResolveReservationDeviceIDArgs = {
	reservationDeviceData: ReservationDeviceStorage | null
	setReservationDeviceData: (value: Partial<ReservationDeviceStorage>) => void
}

/**
 * Returns a persisted device ID from storage or generates and stores a new one.
 * The ID is written to localStorage only on the first resolution.
 */
export const resolveReservationDeviceID = ({ reservationDeviceData, setReservationDeviceData }: ResolveReservationDeviceIDArgs) => {
	const deviceID = reservationDeviceData?.deviceID ?? generateReservationDeviceID()

	if (!reservationDeviceData?.deviceID) {
		setReservationDeviceData({ deviceID })
	}

	return deviceID
}
